import { WIDGET_EVENTS } from './constants';

/**
 * WidgetHandler - Manages widget toggling, custom buttons, and widget state
 */
export class WidgetHandler {
  constructor() {
    this.observer = null;
    this.customButtonSelector = '.lc-widget-toggler';
    this.legacyButtonSelector = '.limechat_widget_toggle';
    this.isWidgetCreated = false;
    this.hasWidgetLoaded = false;

    // Bind methods to maintain context
    this.handleCustomButtonClick = this.handleCustomButtonClick.bind(this);
    this.handleMutations = this.handleMutations.bind(this);
    this.handleWidgetReady = this.handleWidgetReady.bind(this);
  }

  /**
   * Initialize the widget handler
   * @param {Object} config - Configuration object
   * @param {Function} config.toggleWidget - Function to toggle the widget
   * @param {Function} config.loadIframe - Function to load the iframe
   */
  initialize(config) {
    this.toggleWidget = config.toggleWidget;
    this.loadIframe = config.loadIframe;

    this.setupCustomButtons();
    this.setupLegacyButtons();
    this.startObservingDOMChanges();
    this.setupEventListeners();

    // Check if custom buttons exist and preload iframe if needed
    this.handleExistingCustomButtons();

    this.dispatchEvent(WIDGET_EVENTS.INITIALIZED);
  }

  /**
   * Mark widget as created (iframe exists)
   */
  setWidgetCreated() {
    this.isWidgetCreated = true;
    this.updateCustomButtonStates();
    this.dispatchEvent(WIDGET_EVENTS.WIDGET_CREATED);
  }

  /**
   * Mark widget as loaded (content loaded and ready)
   */
  setWidgetLoaded() {
    this.hasWidgetLoaded = true;
    this.updateCustomButtonStates();
    this.dispatchEvent(WIDGET_EVENTS.WIDGET_LOADED);
  }

  /**
   * Get the current widget state
   * @returns {Object} State object with boolean flags
   */
  getWidgetState() {
    return {
      isCreated: this.isWidgetCreated,
      hasLoaded: this.hasWidgetLoaded,
      isReady: this.isReady(),
      hasCustomButtons: this.hasCustomButtons(),
    };
  }

  /**
   * Check if widget is ready for interactions
   * Widget is ready when it's created (iframe exists)
   * @returns {boolean}
   */
  isReady() {
    return this.isWidgetCreated;
  }

  /**
   * Check if widget has fully loaded
   * @returns {boolean}
   */
  isLoaded() {
    return this.hasWidgetLoaded;
  }

  /**
   * Check if custom buttons exist on the page
   * @returns {boolean}
   */
  hasCustomButtons() {
    return document.querySelectorAll(this.customButtonSelector).length > 0;
  }

  /**
   * Setup event listeners for custom buttons
   */
  setupCustomButtons() {
    this.updateCustomButtonStates();
  }

  /**
   * Setup event listeners for legacy buttons
   */
  setupLegacyButtons() {
    document.querySelectorAll(this.legacyButtonSelector).forEach((el) => {
      el.onclick = () => this.handleToggleRequest();
    });
  }

  /**
   * Update custom button states and event listeners
   */
  updateCustomButtonStates() {
    document.querySelectorAll(this.customButtonSelector).forEach((button) => {
      this.setupCustomButton(button);
    });
  }

  /**
   * Setup individual custom button
   * @param {HTMLElement} button - The button element
   */
  setupCustomButton(button) {
    // Remove existing listener to prevent duplicates
    button.removeEventListener('click', this.handleCustomButtonClick);

    // Update button state
    const isReady = this.isReady();
    button.disabled = !isReady;
    button.classList.toggle('lc-widget-ready', isReady);
    button.classList.toggle('lc-widget-disabled', !isReady);

    // Add event listener only if widget is ready
    if (isReady) {
      button.addEventListener('click', this.handleCustomButtonClick);
    }
  }

  /**
   * Handle custom button click events
   * @param {Event} event - Click event
   */
  handleCustomButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    try {
      // Only toggle if widget is ready and loaded
      if (!this.isReady()) {
        this.handleError(new Error('Widget not ready'), 'Custom Button');
        return;
      }

      this.handleToggleRequest();
    } catch (error) {
      this.handleError(error, 'Custom Button');
    }
  }

  /**
   * Handle widget toggle requests with proper state management
   */
  handleToggleRequest() {
    try {
      if (!this.isReady()) {
        // Widget not created yet, try to load iframe first
        if (this.loadIframe) {
          this.loadIframe();
          // Set a timeout to try again after iframe loads
          setTimeout(() => {
            if (this.isReady()) {
              this.performToggle();
            }
          }, 1500);
        }
        return false;
      }

      return this.performToggle();
    } catch (error) {
      this.handleError(error, 'Toggle Request');
      return false;
    }
  }

  /**
   * Perform the actual widget toggle
   */
  performToggle() {
    if (this.toggleWidget) {
      this.toggleWidget();
      this.dispatchEvent(WIDGET_EVENTS.TOGGLE, {
        isOpen: window.$chatwoot?.isOpen,
      });
      return true;
    }
    return false;
  }

  /**
   * Start observing DOM changes for new custom buttons
   */
  startObservingDOMChanges() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new MutationObserver(this.handleMutations);
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Handle DOM mutations to detect new custom buttons
   * @param {Array} mutations - Array of mutation records
   */
  handleMutations(mutations) {
    const hasNewButtons = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.classList?.contains(this.customButtonSelector.slice(1)) ||
            node.querySelector?.(this.customButtonSelector))
      )
    );

    if (hasNewButtons) {
      this.updateCustomButtonStates();

      // If widget isn't created yet and we have custom buttons, preload iframe
      if (!this.isWidgetCreated && this.hasCustomButtons() && this.loadIframe) {
        this.loadIframe();
      }
    }
  }

  /**
   * Setup event listeners for widget events
   */
  setupEventListeners() {
    window.addEventListener(WIDGET_EVENTS.READY, this.handleWidgetReady);
  }

  /**
   * Handle widget ready event
   */
  handleWidgetReady() {
    this.setWidgetLoaded();
  }

  /**
   * Handle existing custom buttons on initialization
   */
  handleExistingCustomButtons() {
    if (this.hasCustomButtons() && this.loadIframe) {
      // Delay iframe loading to avoid blocking initial setup
      setTimeout(() => {
        if (!this.isWidgetCreated) {
          this.loadIframe();
        }
      }, 500);
    }
  }

  /**
   * Dispatch custom events
   * @param {string} eventName - Event name
   * @param {Object} detail - Event detail
   */
  dispatchEvent(eventName, detail = {}) {
    try {
      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: {
            ...detail,
            timestamp: Date.now(),
            widgetState: this.getWidgetState(),
          },
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to dispatch event:', eventName, error);
    }
  }

  /**
   * Handle errors with proper logging and event dispatching
   * @param {Error} error - The error object
   * @param {string} action - The action that caused the error
   */
  handleError(error, action) {
    // eslint-disable-next-line no-console
    console.error(`WidgetHandler error in ${action}:`, error);

    this.dispatchEvent(WIDGET_EVENTS.ERROR, {
      error: error.message,
      action: `${action}`,
      widgetState: this.getWidgetState(),
    });
  }

  /**
   * Clean up resources and event listeners
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    window.removeEventListener(WIDGET_EVENTS.READY, this.handleWidgetReady);

    // Remove event listeners from custom buttons
    document.querySelectorAll(this.customButtonSelector).forEach((button) => {
      button.removeEventListener('click', this.handleCustomButtonClick);
    });

    this.dispatchEvent(WIDGET_EVENTS.DESTROYED);
  }

  /**
   * Reset the widget handler state
   */
  reset() {
    this.isWidgetCreated = false;
    this.hasWidgetLoaded = false;
    this.updateCustomButtonStates();
    this.dispatchEvent(WIDGET_EVENTS.RESET);
  }

  /**
   * Get debug information about the widget handler
   * @returns {Object} Debug information
   */
  getDebugInfo() {
    return {
      ...this.getWidgetState(),
      customButtonCount: document.querySelectorAll(this.customButtonSelector)
        .length,
      legacyButtonCount: document.querySelectorAll(this.legacyButtonSelector)
        .length,
      observerActive: !!this.observer,
      hasToggleFunction: typeof this.toggleWidget === 'function',
      hasLoadFunction: typeof this.loadIframe === 'function',
    };
  }
}
